import { html, unsafeCSS } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ScopedRegistryHost } from '@lit-labs/scoped-registry-mixin';
import { LeafElement } from '@cigna/dcws_leaf-web-components/components/leaf-element.js';
import { LeafButtonLink } from '@cigna/dcws_leaf-web-components/components/button-link/button-link.js';
import { LeafIcon } from '@cigna/dcws_leaf-web-components/components/icon/icon.js';
import { CHCRow } from '@cigna/dcws_leaf-web-components/chc-components/row/row.js';
import { LeafList } from '@cigna/dcws_leaf-web-components/components/list/list.component.js';
import styles from './understanding-medicare-card.scss?inline';

export class CHCUnderstandingMedicareCard extends ScopedRegistryHost(LeafElement) {
  static override styles = unsafeCSS(styles);

  static elementDefinitions = {
    'leaf-button-link': LeafButtonLink,
    'chc-row': CHCRow,
    'leaf-list': LeafList,
    'leaf-icon': LeafIcon,
  };

  @property({ reflect: true })
  variant: '2-col' | 'icon-row' | 'plain' = 'plain';

  renderHeading = () => {
    return html`
      <div class="card__header" slot="heading">
        <slot name="plan-heading">${unsafeHTML(this.title)}</slot>
        <slot name="heading-desc" class="heading-desc"></slot>
      </div>
    `;
  };

  renderPlainCard = () => {
    return html`
      <slot name="content"></slot>
      <slot name="content-desc"></slot>
    `;
  };

  renderCardWithTwoColumns = () => {
    return html`
      <div class="side-lists">
        <div class="left">
          <div class="chart-icon">
            <slot name="left-icon"></slot>
          </div>
          <p class="side-heading"><slot name="left-side-heading"></slot></p>
          <div><slot name="left-content"></slot></div>
          <slot name="left-content-desc"></slot>
        </div>
        <div class="right">
          <div class="chart-icon">
            <slot name="right-icon"></slot>
          </div>
          <p class="side-heading"><slot name="right-side-heading"></slot></p>
          <slot name="right-content"></slot>
          <slot name="right-content-desc"></slot>
        </div>
      </div>
    `;
  };

  renderCardWithIconRow = () => {
    return html`
      <div class="icon-row">
        <div class="chart-icon">
          <slot name="row-icon-1"></slot>
          <span class="side-heading"><slot name="side-heading-1"></slot></span>
        </div>
        <div class="plus">+</div>

        <div class="chart-icon">
          <slot name="row-icon-2"></slot>

          <span class="side-heading"><slot name="side-heading-2"></slot></span>
        </div>
        <div class="plus">+</div>

        <div class="chart-icon">
          <slot name="row-icon-3"></slot>

          <span class="side-heading"><slot name="side-heading-3"></slot></span>
        </div>
      </div>
      <slot name="content"></slot>
      <slot name="content-desc"></slot>
    `;
  };

  override render() {
    const componentClassName = {
      'leaf-c-content-card': true,
      'leaf-c-content-card--icon ': this.variant === 'icon-row' || this.variant === '2-col',
    };

    return html`
      <div class="${classMap(componentClassName)}">
        <div class="inner-wrapper">
          ${this.renderHeading()}
          <div class="card__content" slot="middle">
            ${this.variant === '2-col'
              ? this.renderCardWithTwoColumns()
              : this.variant === 'icon-row'
                ? this.renderCardWithIconRow()
                : this.renderPlainCard()}
          </div>
          <div class="card__footer" slot="call-to-action">
            <slot name="cta"></slot>
          </div>
        </div>
      </div>
    `;
  }
}
